import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import moment from 'moment';
import { FullScreen } from '@pv/common/components';
import { AdminReport, SearchBar } from '../reports';
import {
  ADMIN_OAUTH_APPLICATIONS_QUERY,
  oauthApplicationState,
} from './graphql';
import { OauthApplicationFormDialog } from './OauthApplicationFormDialog';

interface OauthApplicationsTableProps {
  organizationId?: string;
  organizationPublicId?: string;
}

const formatDate = (params: GridValueFormatterParams) =>
  params.value ? moment(params.value).format('MM/DD/YYYY') : 'Never';

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

export const OauthApplicationsTable = ({
  organizationId,
  organizationPublicId,
}: OauthApplicationsTableProps) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [awaitingReview, setAwaitingReview] = useState(false);
  const [creating, setCreating] = useState(false);

  const additionalQueryVariables = useMemo(
    () => ({
      search: search || undefined,
      organizationId,
      awaitingReview: awaitingReview || undefined,
    }),
    [search, organizationId, awaitingReview]
  );

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          component={RouterLink}
          to={`/admin/oauth-applications/${params.row.id}`}
          underline="hover"
          onClick={stopPropagation}
        >
          {params.row.name}
        </Link>
      ),
    },
    {
      field: 'organization',
      headerName: 'Owner',
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row.organization ? (
          <Link
            component={RouterLink}
            to={`/admin/organizations/${params.row.organization.id}/info`}
            underline="hover"
            onClick={stopPropagation}
          >
            {params.row.organization.name}
          </Link>
        ) : (
          'None'
        ),
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 1,
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        oauthApplicationState(params.row),
    },
    { field: 'clientId', headerName: 'Client ID', flex: 1, sortable: false },
    {
      field: 'lastTokenIssuedAt',
      headerName: 'Last token issued',
      flex: 1,
      sortable: false,
      valueFormatter: formatDate,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 1,
      valueFormatter: formatDate,
    },
  ];

  return (
    <>
      <FullScreen paddingBottom={24}>
        {({ height }) => (
          <Paper sx={{ height, width: '100%' }}>
            <AdminReport
              reportName="adminOauthApplications"
              columns={columns}
              title="OAuth apps"
              toolbar={true}
              query={ADMIN_OAUTH_APPLICATIONS_QUERY}
              additionalQueryVariables={additionalQueryVariables}
              renderCustomSearchBar={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SearchBar
                    onSearchChange={setSearch}
                    placeholder="Search OAuth apps by name..."
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={awaitingReview}
                        onChange={(event) =>
                          setAwaitingReview(event.target.checked)
                        }
                      />
                    }
                    label="Awaiting review only"
                  />
                </Box>
              )}
              downloadButton={
                <Button variant="contained" onClick={() => setCreating(true)}>
                  New app
                </Button>
              }
              onRowClick={(params: GridRowParams) =>
                navigate(`/admin/oauth-applications/${params.row.id}`)
              }
            />
          </Paper>
        )}
      </FullScreen>
      {creating && (
        <OauthApplicationFormDialog
          open
          defaultOrganizationPublicId={organizationPublicId}
          onClose={() => setCreating(false)}
          onSaved={(oauthApplication) =>
            navigate(`/admin/oauth-applications/${oauthApplication.id}`)
          }
        />
      )}
    </>
  );
};
